import React, { useState, useCallback, useEffect } from 'react';

export default function CrystalGlass({ autoBreak = false, onBreak }) {
    const [phase, setPhase] = useState('intact');
    const [shards, setShards] = useState([]);
    const [cracks, setCracks] = useState([]);
    const [ripples, setRipples] = useState([]);

    const generateCracks = () => {
        const newCracks = [];
        const numCracks = 12;

        for (let i = 0; i < numCracks; i++) {
            const angle = (i / numCracks) * 360 + (Math.random() - 0.5) * 30;
            const length = 30 + Math.random() * 50;
            const segments = [];
            let x = 50;
            let y = 50;

            for (let j = 0; j < 4; j++) {
                const segLength = length / 4;
                const deviation = (Math.random() - 0.5) * 20;
                const rad = ((angle + deviation) * Math.PI) / 180;
                const newX = x + Math.cos(rad) * segLength;
                const newY = y + Math.sin(rad) * segLength;
                segments.push({ x1: x, y1: y, x2: newX, y2: newY });
                x = newX;
                y = newY;
            }

            newCracks.push({
                id: i,
                segments,
                delay: Math.random() * 0.15,
            });
        }
        return newCracks;
    };

    const generateShards = () => {
        const newShards = [];

        for (let i = 0; i < 15; i++) {
            const angle = Math.random() * 360;
            const velocity = 200 + Math.random() * 300;
            const rad = (angle * Math.PI) / 180;

            newShards.push({
                id: i,
                type: 'large',
                velocityX: Math.cos(rad) * velocity,
                velocityY: Math.sin(rad) * velocity - 100,
                rotation: Math.random() * 1080 - 540,
                scale: 0.6 + Math.random() * 0.8,
                shape: Math.floor(Math.random() * 4),
                delay: Math.random() * 0.1,
                gravity: 600 + Math.random() * 200,
                hue: Math.random() * 30 - 15,
            });
        }

        for (let i = 0; i < 30; i++) {
            const angle = Math.random() * 360;
            const velocity = 100 + Math.random() * 400;
            const rad = (angle * Math.PI) / 180;

            newShards.push({
                id: 100 + i,
                type: 'small',
                velocityX: Math.cos(rad) * velocity,
                velocityY: Math.sin(rad) * velocity - 150,
                rotation: Math.random() * 1440 - 720,
                scale: 0.2 + Math.random() * 0.4,
                delay: Math.random() * 0.15,
                gravity: 500 + Math.random() * 300,
            });
        }

        for (let i = 0; i < 40; i++) {
            const angle = Math.random() * 360;
            const velocity = 50 + Math.random() * 200;
            const rad = (angle * Math.PI) / 180;

            newShards.push({
                id: 200 + i,
                type: 'dust',
                velocityX: Math.cos(rad) * velocity,
                velocityY: Math.sin(rad) * velocity - 50,
                scale: 0.5 + Math.random() * 1,
                delay: Math.random() * 0.2,
                duration: 0.8 + Math.random() * 0.6,
            });
        }

        return newShards;
    };

    const breakGlass = useCallback(() => {
        if (phase !== 'intact') return;

        setPhase('cracking');
        setCracks(generateCracks());
        setRipples([{ id: Date.now() }]);

        setTimeout(() => {
            setPhase('exploding');
            setShards(generateShards());
            onBreak && onBreak();
        }, 400);

        setTimeout(() => {
            setPhase('broken');
        }, 2000);

    }, [phase, onBreak]);

    const reset = () => {
        setPhase('intact');
        setShards([]);
        setCracks([]);
        setRipples([]);
    };

    useEffect(() => {
        if (autoBreak && phase === 'intact') {
            const timer = setTimeout(() => {
                breakGlass();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [autoBreak, phase, breakGlass]);

    return (
        <div >
            {/* Ambient light effect */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
                <div className="absolute top-1/3 left-1/3 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl" />
            </div>

            <div className="relative flex items-center justify-center"  >

                {/* Flash lumineux a l'impact */}
                {(phase === 'cracking' || phase === 'exploding') && (
                    <div className="absolute inset-0 pointer-events-none">
                        <div
                            className="absolute w-full h-full animate-glass-flash"
                            style={{
                                background: 'radial-gradient(circle at 50% 40%, rgba(255,100,100,0.8) 0%, transparent 50%)',
                            }}
                        />
                    </div>
                )}

                {/* Shockwave ripples */}
                {ripples.map(ripple => (
                    <div
                        key={ripple.id}
                        className="absolute pointer-events-none animate-glass-ripple"
                        style={{
                            left: '50%',
                            top: '40%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        <div className="w-4 h-4 border-2 border-red-400/60 rounded-full" />
                    </div>
                ))}

                {/* Le verre intact ou fissure */}
                <div
                    onClick={breakGlass}
                    className={`relative cursor-pointer transition-all duration-200 ${phase === 'exploding' || phase === 'broken' ? 'opacity-0 scale-110' : 'opacity-100'
                        } ${phase === 'intact' ? 'hover:scale-105' : ''}`}
                >
                    <svg width="160" height="320" viewBox="0 0 160 320" className="drop-shadow-2xl">
                        <defs>
                            <linearGradient id="crystalBody" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="rgba(255,255,255,0.15)" />
                                <stop offset="25%" stopColor="rgba(255,200,200,0.25)" />
                                <stop offset="50%" stopColor="rgba(255,255,255,0.35)" />
                                <stop offset="75%" stopColor="rgba(255,180,180,0.2)" />
                                <stop offset="100%" stopColor="rgba(255,255,255,0.1)" />
                            </linearGradient>

                            <linearGradient id="wineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="rgba(180,0,40,0.85)" />
                                <stop offset="100%" stopColor="rgba(120,0,30,0.9)" />
                            </linearGradient>

                            <linearGradient id="shineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="rgba(255,255,255,0.7)" />
                                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                            </linearGradient>

                            <filter id="glowRed">
                                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                                <feMerge>
                                    <feMergeNode in="coloredBlur" />
                                    <feMergeNode in="SourceGraphic" />
                                </feMerge>
                            </filter>

                            <filter id="innerGlow">
                                <feGaussianBlur stdDeviation="3" result="blur" />
                                <feComposite in="SourceGraphic" in2="blur" operator="over" />
                            </filter>
                        </defs>

                        {/* Coupe du verre */}
                        <path
                            d="M30 20
                 Q25 25 25 35
                 Q25 100 45 145
                 Q55 165 80 175
                 Q105 165 115 145
                 Q135 100 135 35
                 Q135 25 130 20
                 Q120 15 80 15
                 Q40 15 30 20 Z"
                            fill="url(#crystalBody)"
                            stroke="rgba(255,255,255,0.5)"
                            strokeWidth="1.5"
                            filter="url(#innerGlow)"
                        />

                        {/* Vin dans le verre */}
                        <path
                            d="M38 70
                 Q35 100 50 135
                 Q60 155 80 162
                 Q100 155 110 135
                 Q125 100 122 70
                 Q115 65 80 65
                 Q45 65 38 70 Z"
                            fill="url(#wineGradient)"
                        />

                        {/* Surface du vin - reflet */}
                        <ellipse cx="80" cy="68" rx="42" ry="10" fill="rgba(200,150,150,0.3)" />
                        <ellipse cx="80" cy="66" rx="35" ry="7" fill="rgba(255,200,200,0.15)" />

                        {/* Reflets principaux sur le verre */}
                        <path
                            d="M40 25 Q38 50 45 100 Q48 120 52 135"
                            fill="none"
                            stroke="url(#shineGradient)"
                            strokeWidth="4"
                            strokeLinecap="round"
                            opacity="0.8"
                        />

                        <path
                            d="M45 30 Q44 45 48 70"
                            fill="none"
                            stroke="rgba(255,255,255,0.6)"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />

                        <ellipse cx="115" cy="50" rx="6" ry="15" fill="rgba(255,255,255,0.2)" transform="rotate(15 115 50)" />

                        {/* Bord superieur lumineux */}
                        <ellipse cx="80" cy="18" rx="50" ry="8" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2" filter="url(#glowRed)" />
                        <ellipse cx="80" cy="17" rx="45" ry="6" fill="rgba(255,255,255,0.05)" />

                        {/* Tige */}
                        <rect x="76" y="175" width="8" height="100" fill="url(#crystalBody)" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />
                        <rect x="78" y="177" width="2" height="96" fill="rgba(255,255,255,0.3)" />

                        {/* Noeud decoratif sur la tige */}
                        <ellipse cx="80" cy="275" rx="12" ry="7" fill="url(#crystalBody)" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5" />
                        <ellipse cx="80" cy="175" rx="8" ry="4" fill="rgba(255,255,255,0.15)" />

                        {/* Pied/Base */}
                        <ellipse cx="80" cy="275" rx="10" ry="4" fill="url(#crystalBody)" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />
                        <path
                            d="M70 275 Q65 280 50 290 Q45 295 45 300 L115 300 Q115 295 110 290 Q95 280 90 275"
                            fill="url(#crystalBody)"
                            stroke="rgba(255,255,255,0.4)"
                            strokeWidth="1"
                        />
                        <ellipse cx="80" cy="300" rx="45" ry="10" fill="url(#crystalBody)" stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
                        <ellipse cx="80" cy="298" rx="38" ry="7" fill="rgba(255,255,255,0.08)" />
                        <path
                            d="M55 295 Q60 290 65 287"
                            fill="none"
                            stroke="rgba(255,255,255,0.4)"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />

                        {/* Fissures */}
                        {phase !== 'intact' && cracks.map(crack => (
                            <g key={crack.id}>
                                {crack.segments.map((seg, idx) => (
                                    <line
                                        key={idx}
                                        x1={seg.x1 + 30}
                                        y1={seg.y1 + 50}
                                        x2={seg.x2 + 30}
                                        y2={seg.y2 + 50}
                                        stroke="rgba(255,255,255,0.9)"
                                        strokeWidth="1.5"
                                        className="animate-glass-crack"
                                        style={{
                                            animationDelay: `${crack.delay + idx * 0.05}s`,
                                            filter: 'url(#glowRed)',
                                        }}
                                    />
                                ))}
                            </g>
                        ))}
                    </svg>
                </div>

                {/* Eclats avec physique */}
                {shards.filter(s => s.type === 'large').map((shard) => (
                    <div
                        key={shard.id}
                        className="absolute pointer-events-none"
                        style={{
                            left: '50%',
                            top: '30%',
                            '--velocity-x': `${shard.velocityX}px`,
                            '--velocity-y': `${shard.velocityY}px`,
                            '--rotation': `${shard.rotation}deg`,
                            '--gravity': `${shard.gravity}px`,
                            '--scale': shard.scale,
                            animation: `shardPhysics 1.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${shard.delay}s forwards`,
                        }}
                    >
                        <svg width="50" height="60" viewBox="0 0 50 60" style={{ filter: 'drop-shadow(0 0 10px rgba(255,100,100,0.3))' }}>
                            <defs>
                                <linearGradient id={`shardGrad${shard.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor={`rgba(${220 + shard.hue},${200 + shard.hue},200,0.6)`} />
                                    <stop offset="50%" stopColor="rgba(255,255,255,0.8)" />
                                    <stop offset="100%" stopColor={`rgba(${200 + shard.hue},${180 + shard.hue},180,0.4)`} />
                                </linearGradient>
                            </defs>
                            {shard.shape === 0 && (
                                <polygon points="25,0 48,20 40,58 10,50 2,15" fill={`url(#shardGrad${shard.id})`} stroke="rgba(255,255,255,0.8)" strokeWidth="1" />
                            )}
                            {shard.shape === 1 && (
                                <polygon points="15,2 45,8 42,45 20,58 5,35" fill={`url(#shardGrad${shard.id})`} stroke="rgba(255,255,255,0.8)" strokeWidth="1" />
                            )}
                            {shard.shape === 2 && (
                                <polygon points="8,10 35,0 48,25 38,55 12,48" fill={`url(#shardGrad${shard.id})`} stroke="rgba(255,255,255,0.8)" strokeWidth="1" />
                            )}
                            {shard.shape === 3 && (
                                <polygon points="20,0 45,15 40,50 8,55 0,20" fill={`url(#shardGrad${shard.id})`} stroke="rgba(255,255,255,0.8)" strokeWidth="1" />
                            )}
                        </svg>
                    </div>
                ))}

                {/* Petits eclats */}
                {shards.filter(s => s.type === 'small').map((shard) => (
                    <div
                        key={shard.id}
                        className="absolute pointer-events-none"
                        style={{
                            left: '50%',
                            top: '30%',
                            '--velocity-x': `${shard.velocityX}px`,
                            '--velocity-y': `${shard.velocityY}px`,
                            '--rotation': `${shard.rotation}deg`,
                            '--gravity': `${shard.gravity}px`,
                            '--scale': shard.scale,
                            animation: `shardPhysics 1.5s ease-out ${shard.delay}s forwards`,
                        }}
                    >
                        <div
                            className="w-3 h-4 bg-gradient-to-br from-white/70 to-red-200/50"
                            style={{
                                clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)',
                                boxShadow: '0 0 8px rgba(255,200,200,0.5)',
                            }}
                        />
                    </div>
                ))}

                {/* Particules de poussiere brillante */}
                {shards.filter(s => s.type === 'dust').map((shard) => (
                    <div
                        key={shard.id}
                        className="absolute pointer-events-none"
                        style={{
                            left: '50%',
                            top: '30%',
                            '--velocity-x': `${shard.velocityX}px`,
                            '--velocity-y': `${shard.velocityY}px`,
                            '--scale': shard.scale,
                            animation: `dustFloat ${shard.duration}s ease-out ${shard.delay}s forwards`,
                        }}
                    >
                        <div
                            className="w-2 h-2 rounded-full bg-white"
                            style={{
                                boxShadow: '0 0 6px 2px rgba(255,255,255,0.8), 0 0 12px 4px rgba(255,150,150,0.4)',
                            }}
                        />
                    </div>
                ))}

                {/* Eclats de vin */}
                {phase === 'exploding' && Array.from({ length: 20 }).map((_, i) => (
                    <div
                        key={`wine-${i}`}
                        className="absolute pointer-events-none rounded-full"
                        style={{
                            left: '50%',
                            top: '32%',
                            width: 4 + Math.random() * 8,
                            height: 4 + Math.random() * 8,
                            background: `rgba(${100 + Math.random() * 40}, 0, ${20 + Math.random() * 20}, 0.8)`,
                            '--velocity-x': `${(Math.random() - 0.5) * 300}px`,
                            '--velocity-y': `${Math.random() * -200 - 50}px`,
                            '--gravity': `${600 + Math.random() * 200}px`,
                            animation: `wineDroplet 1.2s ease-out ${Math.random() * 0.15}s forwards`,
                        }}
                    />
                ))}
            </div>

            {/* Label "FRAGILE" sous le verre en mode compact */}
            {
                phase === 'intact' && (
                    <div className="absolute bottom-26 px-4 text-red-500 text-xl font-mono tracking-[0.5em] uppercase animate-glass-fadeIn">
                        Fragile
                    </div>
                )
            }

            <style>{`
        @keyframes shardPhysics {
          0% {
            transform: translate(-50%, -50%) rotate(0deg) scale(var(--scale));
            opacity: 1;
          }
          100% {
            transform: translate(
              calc(-50% + var(--velocity-x)),
              calc(-50% + var(--velocity-y) + var(--gravity))
            ) rotate(var(--rotation)) scale(calc(var(--scale) * 0.5));
            opacity: 0;
          }
        }

        @keyframes dustFloat {
          0% {
            transform: translate(-50%, -50%) scale(var(--scale));
            opacity: 1;
          }
          60% {
            opacity: 1;
          }
          100% {
            transform: translate(
              calc(-50% + var(--velocity-x)),
              calc(-50% + var(--velocity-y) - 50px)
            ) scale(0);
            opacity: 0;
          }
        }

        @keyframes wineDroplet {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(
              calc(-50% + var(--velocity-x)),
              calc(-50% + var(--velocity-y) + var(--gravity))
            ) scale(0.3);
            opacity: 0;
          }
        }

        .animate-glass-crack {
          animation: glassCrack 0.2s ease-out forwards;
        }

        @keyframes glassCrack {
          0% {
            stroke-dasharray: 100;
            stroke-dashoffset: 100;
            opacity: 0;
          }
          100% {
            stroke-dasharray: 100;
            stroke-dashoffset: 0;
            opacity: 1;
          }
        }

        .animate-glass-flash {
          animation: glassFlash 0.5s ease-out forwards;
        }

        @keyframes glassFlash {
          0% {
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          100% {
            opacity: 0;
          }
        }

        .animate-glass-ripple {
          animation: glassRipple 0.6s ease-out forwards;
        }

        @keyframes glassRipple {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(40);
            opacity: 0;
          }
        }

        .animate-glass-fadeIn {
          animation: glassFadeIn 0.8s ease-out 0.3s both;
        }

        @keyframes glassFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        </div >
    );
}
